/* eslint-disable max-len */
// src/views/student/ListAccessStudentModal/ListAccessStudentModal.tsx
import React, {
  FC, useCallback, useState, useEffect,
} from 'react';
import {
  Button, Checkbox, Modal, Tooltip, message, Spin,
} from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import classNames from 'classnames';
import { TbInfoCircle, TbList } from 'react-icons/tb';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { DeleteOutlined } from '@ant-design/icons';
import classes from './ListAccessStudentModal.module.less';
import AppModal from '../../../components/AppModal';
import { UserData } from '../../../services/ApiService.types';
import { getListOfWordsList, getIsLoadingWordsListList } from '../../words-lists/slice/selectors';
import {
  refreshWordsListPage,
} from '../../words-lists/slice/actions';
import { getActiveList } from '../../auth/slice/selectors';
import { setActiveList } from '../../auth/slice/actions';
import { getWordsListLink } from '../../../constants/Path';
import { ReactComponent as Edit } from '../../../assets/icons/teacher/edit.svg';
import ApiService from '../../../services/ApiService';
import { pluralForm } from '../../../utils/plural';

type ListAccessStudentModalProps = {
  isVisible: boolean;
  hideModal: () => void;
  student: UserData;
};

const ListAccessStudentModal: FC<ListAccessStudentModalProps> = ({
  student, isVisible, hideModal,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const loading = useSelector(getIsLoadingWordsListList);
  const lists = useSelector(getListOfWordsList);

  const activeList = useSelector(getActiveList);
  const [clickedItem, setClickedItem] = useState<{ id: number, wordsLength: number } | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  // Pola do zaznaczania tylko w trybie „Cofnij wiele” — cofanie wielu udostępnień naraz to rzadki przypadek,
  // jak „Usuń wiele” na ekranie list
  const [isBulkMode, setIsBulkMode] = useState(false);

  // ✅ Załaduj listy gdy modal się otwiera
  useEffect(() => {
    if (isVisible) {
      console.log('📋 Modal opened - refreshing lists...');
      dispatch(refreshWordsListPage());
    }
  }, [isVisible, dispatch]);

  const onCancel = useCallback(() => {
    hideModal();
  }, [hideModal]);

  const filteredLists = React.useMemo(() => {
    console.log('🔍 Filtering for student:', student.id);
    const filtered = lists.filter((list: any) => {
      const hasAccess = list.sharedStudentIds?.includes(student.id);
      if (hasAccess) {
        console.log(`✅ Student has access to: ${list.name} (${list.id})`);
      }

      return hasAccess;
    });
    console.log('📊 Filtered lists count:', filtered.length);

    return filtered.sort((a: any, b: any) => {
    // Znajdź share dla TEGO KONKRETNEGO studenta w każdej liście
      const aShare = a.shares?.find((s: any) => s.studentId === student.id);
      const bShare = b.shares?.find((s: any) => s.studentId === student.id);

      console.log(`🔍 Comparing "${a.name}" vs "${b.name}":`, {
        aShareDate: aShare?.sharedAt,
        bShareDate: bShare?.sharedAt,
      });

      // Priorytet 1: Sortuj po dacie udostępnienia (od najnowszej)
      if (aShare?.sharedAt && bShare?.sharedAt) {
        const diff = new Date(bShare.sharedAt).getTime() - new Date(aShare.sharedAt).getTime();
        console.log(`  → By sharedAt: ${diff > 0 ? 'b newer' : 'a newer'}`);

        return diff;
      }

      // Priorytet 2: Jeśli jedna lista ma datę, a druga nie - z datą na górze
      if (aShare?.sharedAt && !bShare?.sharedAt) {
        console.log('  → a has date, b does not');

        return -1;
      }
      if (!aShare?.sharedAt && bShare?.sharedAt) {
        console.log('  → b has date, a does not');

        return 1;
      }

      // Priorytet 3: Fallback na updatedAt listy
      if (a.updatedAt && b.updatedAt) {
        const diff = new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
        console.log(`  → By updatedAt: ${diff > 0 ? 'b newer' : 'a newer'}`);

        return diff;
      }

      // Priorytet 4: Fallback na ID
      console.log('  → By ID');

      return b.id - a.id;
    });
  }, [lists, student.id]);

  // Tylko listy widoczne w okienku — po odświeżeniu nie wyślemy numerów, których już tu nie ma
  const selectedLists = React.useMemo(
    () => filteredLists.filter((list: any) => selectedIds.includes(list.id)),
    [filteredLists, selectedIds],
  );
  const allSelected = filteredLists.length > 0 && selectedLists.length === filteredLists.length;
  const nothingSelected = selectedLists.length === 0;

  const toggleSelected = useCallback((id: number) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]));
  }, []);

  const toggleSelectAll = useCallback((e: CheckboxChangeEvent) => {
    setSelectedIds(e.target.checked ? filteredLists.map((list: any) => list.id) : []);
  }, [filteredLists]);

  const startBulkMode = useCallback(() => setIsBulkMode(true), []);

  // Wyjście z trybu zaznaczania czyści zaznaczenie
  const cancelBulkMode = useCallback(() => {
    setIsBulkMode(false);
    setSelectedIds([]);
  }, []);

  const toggleActiveWordsList = useCallback((item) => {
    if (!activeList && item.words.length >= 2) {
      dispatch(setActiveList(item));
    } else if (activeList && activeList.id === item.id) {
      dispatch(setActiveList(null));
    } else if (activeList && item.words.length >= 2) {
      dispatch(setActiveList(item));
    }

    if (item.words.length < 2) {
      setClickedItem({ id: item.id, wordsLength: item.words.length });
      setTimeout(() => setClickedItem(null), 2500);
    } else {
      setClickedItem(null);
    }
  }, [activeList, dispatch]);

  // W trybie zaznaczania kliknięcie nazwy zaznacza listę, jak na ekranie list; poza nim przełącza aktywną listę
  const onRowClick = useCallback((item: any) => {
    if (isBulkMode) {
      toggleSelected(item.id);
    } else {
      toggleActiveWordsList(item);
    }
  }, [isBulkMode, toggleSelected, toggleActiveWordsList]);

  const handleEditList = useCallback((item: any) => {
    history.push(getWordsListLink(item.id));
    hideModal();
  }, [history, hideModal]);

  // Także pojedyncze cofnięcie idzie przez revoke, nie przez przełączanie: przy nieaktualnym
  // stanie okienka przełączanie udostępniłoby listę z powrotem.
  const revokeAccess = useCallback(async (items: any[]) => {
    const ids = items.map((item) => item.id);

    try {
      dispatch(setActiveList(null));
      await ApiService.wordsList.revokeSharesFromStudent(student.id, ids);
      dispatch(refreshWordsListPage());
      // Cofnięte listy znikają z okienka — zaznaczać nie ma już czego, tryb zaznaczania się kończy. Pojedyncze
      // cofnięcie (kosz w wierszu) zdarza się tylko poza tym trybem, gdy zaznaczenie i tak jest puste
      setSelectedIds([]);
      setIsBulkMode(false);
      message.success(ids.length === 1 ? t('student:access.revoked.one') : t('student:access.revoked.many', { n: ids.length }));
    } catch (error) {
      console.error('Error revoking access:', error);
      message.error(t('student:access.revoked.error'));
    }
  }, [student.id, dispatch, t]);

  const confirmRevoke = useCallback((items: any[]) => {
    const studentName = `${student.name} ${student.surname}`;

    Modal.confirm({
      title: t('student:access.confirm.title'),
      content: items.length === 1
        ? t('student:access.confirm.one', { list: items[0].name, student: studentName })
        : t('student:access.confirm.many', { n: items.length, student: studentName }),
      okText: t('student:access.confirm.ok'),
      cancelText: t('common:buttons.cancel'),
      okButtonProps: { danger: true },
      onOk: () => revokeAccess(items),
    });
  }, [student, revokeAccess, t]);

  return (
    <AppModal title={t('student:access.title')} visible={isVisible} onCancel={onCancel} wide>
      <div className={classes.content}>
        {/* Uczeń jak w oknie „Aktywność”: inicjały w morskim kółku, imię i nazwisko */}
        <div className={classes.student}>
          <span className={classes.avatar}>{student.name[0].toUpperCase()}{student.surname[0].toUpperCase()}</span>
          <span className={classes.studentName}>{student.name} {student.surname}</span>
        </div>

        {/* Listy w karcie jak uczniowie w oknie „Udostępnij”: napis na jasnym pasku, liczba list w plakietce */}
        <div className={classes.card}>
          <div className={classes.cardHeader}>
            <span className={classes.cardTitle}>{t('student:access.lists')}</span>
            <span className={classNames(classes.counter, { [classes.noCount]: filteredLists.length === 0 })}>
              {filteredLists.length}
            </span>
          </div>

          {loading ? (
            <div className={classes.loading}>
              <Spin size="small" />
            </div>
          ) : (
            <>
              {filteredLists.length > 0 ? (
                filteredLists.map((item: any) => (
                  <div
                    key={item.id}
                    className={classNames(classes.listItemContainer, {
                      [classes.activeRow]: item.id === activeList?.id,
                      [classes.tooFewItems]: item.words.length < 2,
                    })}
                  >
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => onRowClick(item)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onRowClick(item);
                        }
                      }}
                      className={classes.itemStyle}
                    >
                      <p title={item.name} className={`${classes.listLabel} ${clickedItem && clickedItem.id === item.id && clickedItem.wordsLength < 3 ? `${classes.clickItem} ${classes['clickedItem-animation']}` : ''}`}>
                        {item.name}
                      </p>
                      <span className={classNames(classes.counter, classes.itemsCount)}>
                        {t(`words-list:itemsCount.${pluralForm(item.words.length)}`, { n: item.words.length })}
                      </span>
                    </div>

                    {/* W trybie zaznaczania po prawej pole do zaznaczania zamiast ikon, w tym samym miejscu — nazwa listy
                        nie przesuwa się przy włączaniu trybu */}
                    {isBulkMode ? (
                      <Checkbox
                        className={classes.rowCheckbox}
                        checked={selectedIds.includes(item.id)}
                        onChange={() => toggleSelected(item.id)}
                        aria-label={t('student:access.selectList', { list: item.name })}
                      />
                    ) : (
                      <div className={classes.actions}>
                        <button
                          className={classes.actionButton}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditList(item);
                          }}
                          aria-label={t('student:access.editList', { list: item.name })}
                        >
                          <Tooltip title={t('student:access.editListTooltip')} placement="top">
                            <span><Edit className={classes.editIcon} aria-hidden="true" /></span>
                          </Tooltip>
                        </button>

                        <button
                          className={classNames(classes.actionButton, classes.deleteButton)}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            confirmRevoke([item]);
                          }}
                          aria-label={t('student:access.revokeList', { list: item.name })}
                        >
                          <Tooltip title={t('student:access.revokeTooltip')} placement="bottom">
                            <span><DeleteOutlined className={classes.deleteIcon} aria-hidden="true" /></span>
                          </Tooltip>
                        </button>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className={classes.emptyState}>
                  <TbList className={classes.emptyIcon} />
                  <p>{t('student:access.empty')}</p>
                </div>
              )}

              {/* Stopka karty — rzadka akcja na końcu, przy długiej liście przyklejona do dołu ekranu. Zwykle sam
                  „Cofnij wiele” na środku. W trybie zaznaczania: „Anuluj” i „Cofnij (n)” po lewej, a „wszystkie” po
                  prawej, w kolumnie pól w wierszach */}
              {filteredLists.length > 0 && (
                <div className={classes.bulkBar}>
                  {isBulkMode ? (
                    <>
                      <div className={classes.bulkActions}>
                        <Button className={classes.barButton} onClick={cancelBulkMode}>
                          {t('common:buttons.cancel')}
                        </Button>
                        <Button
                          type="primary"
                          danger
                          className={classes.barButton}
                          disabled={nothingSelected}
                          onClick={() => confirmRevoke(selectedLists)}
                        >
                          {t('student:access.revokeSelected', { n: selectedLists.length })}
                        </Button>
                      </div>
                      <Checkbox
                        className={classes.selectAllCheckbox}
                        checked={allSelected}
                        onChange={toggleSelectAll}
                      >
                        {t('student:access.all')}
                      </Checkbox>
                    </>
                  ) : (
                    <Button className={`${classes.barButton} ${classes.bulkStart}`} onClick={startBulkMode}>
                      {t('student:access.revokeMany')}
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <p className={classes.footnote}>
          <TbInfoCircle className={classes.footnoteIcon} />
          <span>{t('student:access.hint')}</span>
        </p>
      </div>
    </AppModal>
  );
};

export default ListAccessStudentModal;
